import { Trans, useLingui } from '@lingui/react/macro';
import { Download, Loader2, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { useDeleteModelFile, useTaskProgress, useUploadModelFile } from '@/api/hooks';
import { downloadModelFile } from '@/api/modelFiles';
import { ConfirmModal } from '@/components/ConfirmModal';
import { Dropzone } from '@/components/Dropzone';
import { ModelPreviewImage } from '@/components/ModelPreviewImage';
import { Button } from '@/components/ui/button';
import { Card, CardAction, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import type { ModelFileInfo } from '@/types/api';
import { apiErrorMessage } from '@/utils/apiError';
import { downloadBlob } from '@/utils/download';
import { getProgressMessage } from '@/utils/taskProgress';

export const ModelFileCard = ({
  label,
  model,
  projectId,
  fileType,
  confirmTitle,
  disabled,
}: {
  label: string;
  model: ModelFileInfo | null;
  projectId: string;
  fileType: 'target' | 'blank';
  confirmTitle: string;
  disabled?: boolean;
}): React.ReactElement => {
  const [showConfirm, setShowConfirm] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const { t } = useLingui();

  const deleteModelFileMutation = useDeleteModelFile(projectId);
  const uploadModelFileMutation = useUploadModelFile(projectId);

  const handleDrop = async (acceptedFiles: File[]): Promise<void> => {
    try {
      await uploadModelFileMutation.mutateAsync({ fileType, file: acceptedFiles[0] });
    } catch (e) {
      toast.error(apiErrorMessage(e, t`Failed to upload model file.`));
    }
  };

  const handleDelete = (): void => {
    if (!model) return;
    deleteModelFileMutation.mutate(model.id, {
      onSuccess: () => setShowConfirm(false),
      onError: (e) => {
        toast.error(apiErrorMessage(e, t`Failed to remove model file.`));
        setShowConfirm(false);
      },
    });
  };

  const progress = useTaskProgress(model?.background_task_id ?? null);
  const isGenerating = progress !== null && progress.state !== 'done' && progress.state !== 'failed';
  const { label: progressLabel } = getProgressMessage(progress);

  const isDeleting = deleteModelFileMutation.isPending;
  const isUploading = uploadModelFileMutation.isPending;

  const handleDownload = async (): Promise<void> => {
    if (!model) return;
    setIsDownloading(true);
    try {
      const blob = await downloadModelFile(model.id);
      downloadBlob(URL.createObjectURL(blob), model.original_filename ?? 'model.step');
    } catch (e) {
      toast.error(apiErrorMessage(e, t`Failed to download model file.`));
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle>{label}</CardTitle>
          {fileType === 'blank' && !model && (
            <CardDescription>
              <Trans>Optional — if left empty, a solid box the size of the target model is used.</Trans>
            </CardDescription>
          )}
          <CardAction>
            {isUploading && !model && <Loader2 size={14} className="animate-spin text-slate-400" />}
            {model && !isUploading && (
              <>
                <Button
                  type="button"
                  size="icon"
                  variant="ghost"
                  onClick={handleDownload}
                  disabled={isDownloading}
                  title={t`Download`}
                >
                  {isDownloading ? <Loader2 className="animate-spin" /> : <Download />}
                </Button>
                <span title={disabled === true ? t`Simulation in progress - modifications disabled` : t`Remove`} className={disabled === true ? 'inline-flex cursor-not-allowed' : 'inline-flex'}>
                  <Button
                    type="button"
                    size="icon"
                    variant="ghost"
                    className="hover:text-red-600"
                    onClick={() => setShowConfirm(true)}
                    disabled={disabled === true || isDeleting || isUploading || isDownloading}
                  >
                    {isDeleting ? <Loader2 className="animate-spin" /> : <Trash2 />}
                  </Button>
                </span>
              </>
            )}
          </CardAction>
        </CardHeader>
        <CardContent className="flex flex-col h-full">
          {isGenerating ? (
            <div className="flex h-64 w-full flex-col items-center justify-center gap-2">
              <Loader2 size={20} className="animate-spin text-slate-400" />
              <span className="text-sm text-slate-400">{progressLabel}</span>
            </div>
          ) : model ? (
            <>
              <ModelPreviewImage modelId={model.id} alt={model.original_filename ?? label} className="h-64 w-full" />
              <div className="flex items-baseline gap-2">
                <div className="truncate font-mono text-slate-800">{model.original_filename}</div>
                {model.file_format && <div className="text-sm text-slate-400">{model.file_format}</div>}
              </div>
            </>
          ) : (
            <Dropzone
              accept={{ 'application/step': ['.stp', '.step'] }}
              onDrop={handleDrop}
              disabled={disabled}
              isLoading={isUploading}
              hint=".stp, .step"
              className="flex-1"
            />
          )}
        </CardContent>
      </Card>
      <ConfirmModal
        open={showConfirm}
        onOpenChange={setShowConfirm}
        onConfirm={handleDelete}
        title={confirmTitle}
        message={t`This action cannot be undone. The model file will be permanently deleted.`}
        confirmLabel={t`Remove`}
        confirmVariant="danger"
        isLoading={isDeleting}
      />
    </>
  );
};
